/* ==========================================================================
   RAM Bikes — mobile navigation drawer
   ==========================================================================

   WHAT THIS DOES
   Opens and closes the off-canvas menu that replaces the header nav below
   1280px, and makes it behave like a modal dialog while it is open:

     - focus is trapped inside the panel
     - Escape closes it
     - the backdrop and the close button close it
     - the page behind does not scroll, and does not lose its scroll position
     - focus returns to the burger that opened it
     - it closes itself if the viewport grows back past the breakpoint

   WHY A HAND-ROLLED TRAP AND NOT <dialog>
   `showModal()` would give the trap and the Escape handling for free, but it
   also puts the element in the top layer with a UA-controlled ::backdrop and
   its own centring, none of which survives being styled into a full-height
   edge-anchored panel without fighting the UA stylesheet. The trap below is
   twenty lines. That trade is worth it.

   MARKUP CONTRACT
     #site-drawer                     the root, carries [hidden] when closed
     .drawer__panel                   the dialog element itself
     [aria-controls="site-drawer"]    the button that opens it
     [data-drawer-close]              anything that should close it

   LOAD ORDER
   Deferred — nothing here needs to run before first paint, and the drawer is
   display:none until it does.
   ========================================================================== */

(function () {
  'use strict';

  var drawer = document.getElementById('site-drawer');
  var opener = document.querySelector('[aria-controls="site-drawer"]');
  if (!drawer || !opener) return;

  var panel = drawer.querySelector('.drawer__panel');
  var docEl = document.documentElement;

  // Must match the header ladder in layout.css: above it the horizontal nav
  // is visible and the drawer must not be. It has moved three times now
  // (992, 1200, 1280) and was left behind at 62em on the first two, which
  // meant a 1024-wide window with the drawer open and then resized never
  // closed it. Change one, change the other.
  var DESKTOP = window.matchMedia('(min-width: 80em)');

  var isOpen = false;
  var lastFocused = null;
  var closeTimer = null;

  /* Everything focusable, in DOM order. Recomputed on each Tab because the
     accordions add and remove focusable links as they open and close — a
     list cached at open time goes stale the moment someone expands one. */
  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  /* Visibility test. `offsetParent !== null` is the usual shorthand and it is
     WRONG here: a closed <details> hides its contents with content-visibility
     rather than display:none, so its seven links still report an offsetParent
     and end up in the trap. Tab then wraps to a link nobody can see.

     checkVisibility() with contentVisibilityAuto covers that case directly.
     getClientRects() is the fallback for browsers without it — an element in
     a closed <details> generates no boxes, so the list is empty. */
  function isVisible(el) {
    if (typeof el.checkVisibility === 'function') {
      return el.checkVisibility({
        checkVisibilityCSS: true,
        contentVisibilityAuto: true,
        opacityProperty: true
      });
    }
    return el.getClientRects().length > 0;
  }

  function focusables() {
    var all = panel.querySelectorAll(FOCUSABLE);
    return Array.prototype.filter.call(all, isVisible);
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* The scrollbar disappears when <html> goes overflow:hidden, and the page
     behind jumps sideways by its width. Measure it before locking so the CSS
     can pad the gap back in. Zero on overlay-scrollbar platforms. */
  function lockScroll() {
    var gap = window.innerWidth - docEl.clientWidth;
    docEl.style.setProperty('--scrollbar-w', gap > 0 ? gap + 'px' : '0px');
    docEl.classList.add('is-drawer-open');
  }

  function unlockScroll() {
    docEl.classList.remove('is-drawer-open');
    docEl.style.removeProperty('--scrollbar-w');
  }

  function open() {
    if (isOpen) return;
    isOpen = true;

    if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }

    lastFocused = document.activeElement;

    drawer.hidden = false;
    lockScroll();

    // Force a reflow between removing [hidden] and adding .is-open, otherwise
    // the browser coalesces the two style changes into one and the panel
    // appears in place instead of sliding in.
    void drawer.offsetWidth;

    drawer.classList.add('is-open');
    opener.setAttribute('aria-expanded', 'true');
    opener.setAttribute('aria-label', 'Cerrar menú');

    var first = focusables()[0];
    if (first) first.focus();

    document.addEventListener('keydown', onKeydown, true);
  }

  function close(options) {
    if (!isOpen) return;
    isOpen = false;

    drawer.classList.remove('is-open');
    opener.setAttribute('aria-expanded', 'false');
    opener.setAttribute('aria-label', 'Abrir menú');

    document.removeEventListener('keydown', onKeydown, true);
    unlockScroll();

    // Move focus out before [hidden] lands. A focused element inside a
    // display:none subtree gets focus reset to <body>, which drops the
    // keyboard user back at the top of the page.
    //
    // The second attempt is not belt-and-braces, it is the common path:
    // .focus() on an element that is not focusable — <body>, which is what
    // document.activeElement reports when the drawer was opened by a mouse
    // click that did not move focus — silently does nothing and leaves focus
    // where it was, i.e. inside the panel we are about to hide.
    if (!(options && options.silent)) {
      var target = (lastFocused && document.contains(lastFocused)) ? lastFocused : opener;
      target.focus();
      if (document.activeElement !== target) opener.focus();
    } else if (panel.contains(document.activeElement)) {
      document.activeElement.blur();
    }

    // Let the slide-out finish before hiding, or hide immediately when the
    // reader has asked for no motion.
    var delay = prefersReducedMotion() ? 0 : 240;
    closeTimer = setTimeout(function () {
      drawer.hidden = true;
      closeTimer = null;
    }, delay);
  }

  function onKeydown(event) {
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key !== 'Tab') return;

    var items = focusables();
    if (!items.length) {
      event.preventDefault();
      return;
    }

    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;

    // Wrap at both ends, and pull focus back in if it has escaped the panel
    // entirely (which browsers can do after the DOM changes under it).
    if (!panel.contains(active)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  opener.addEventListener('click', function () {
    if (isOpen) close(); else open();
  });

  drawer.addEventListener('click', function (event) {
    if (event.target.closest('[data-drawer-close]')) close();
  });

  /* Same-page anchors do not reload, so the drawer would stay open on top of
     the destination. Links to another page are harmless to close early. */
  drawer.addEventListener('click', function (event) {
    var link = event.target.closest('a[href]');
    if (link && !link.hasAttribute('data-drawer-close')) close({ silent: true });
  });

  /* Crossing back to desktop while the drawer is open would leave a modal
     over a page that already has its navigation visible. `silent` because
     returning focus to a now-hidden burger is worse than leaving it alone. */
  function onBreakpoint(event) {
    if (event.matches) close({ silent: true });
  }

  if (DESKTOP.addEventListener) DESKTOP.addEventListener('change', onBreakpoint);
  else DESKTOP.addListener(onBreakpoint); // Safari < 14

  // Back/forward cache can restore the page with the drawer mid-state.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted && isOpen) close({ silent: true });
  });
})();
